/** ProductSyncForm — the embeddable launch half of Product Sync: fetch a
 *  restech's product catalog (1h Redis cache on the backend; "Force fetch"
 *  bypasses), review tour mappings, edit them, select products and launch the
 *  import task.
 *
 *  The catalogue is never fetched inside a request: a cache hit answers at
 *  once, otherwise a `list_products` background task fetches it while an
 *  inline progress card (cancel, live logs, "open" in place) stands in for the
 *  table, and the listing is read back when the task completes. Imports run through the parallel pipeline (plimport) by
 *  default; "Sequential import" falls back to the one-product-at-a-time loop,
 *  and Advanced exposes the parallelism knobs.
 *
 *  Hosted by ProductSyncPage (which owns the SyncScopePicker and injects it
 *  via `scopeSlot` to keep the original single toolbar row) and by the tenant
 *  setup wizard (fixed scope, `onSubmit`/`onLaunched` injected so the wizard's
 *  preflight item owns the spawned task instead of navigating away).
 *
 *  An integration whose restech keeps per-product catalogue rows (it has a
 *  catalogue settings registry — Ventrata first) gets the catalogue section
 *  (`catalogue/CatalogueSection`): products are chosen, configured and launched
 *  there, Fetch becomes the listing refresh, and the vendor listing with its hand
 *  mappings folds away. Whether anything may be imported is the listing / rows
 *  payload's `can_import`, never the integration's `supports`.
 */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { TASK_STATE, type BackgroundTaskDetail } from '@/api/bgtasks';
import { isCatalogueNotLoaded, isMissingCredentials } from '@/api/client';
import * as opsApi from '@/api/ops';
import type { SyncProductsStartInput } from '@/api/ops';
import * as syncApi from '@/api/sync';
import type {
  ImportBlocker,
  MappingPolicy,
  ProductFacet,
  ProductJoinInvalid,
  ProductListItem,
  ProductMapping,
  ProductsResponse,
} from '@/api/sync';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';
import { TaskDetailsDialog } from '@/features/bgtasks/TaskDetailsDialog';
import { TaskProgressCard } from '@/features/bgtasks/TaskProgressCard';
import { ImportSourceWidget } from '@/features/tour-operators/integrations/config-widgets';
import {
  autoSelectKey,
  ImportKeyFlags,
  lackingKinds,
} from '@/features/tour-operators/integrations/config-widgets/ImportKeyFlags';
import { useImportSources } from '@/features/tour-operators/integrations/config-widgets/hooks';

import { CatalogueSection, type LaunchTarget } from './catalogue/CatalogueSection';
import { catalogueScopeOf, useCatalogueRegistry, useInvalidateCatalogue } from './catalogue/queries';
import { MappingDrawer } from './MappingDrawer';
import { ApplyMappingsDialog } from './ApplyMappingsDialog';
import { CombineProductsDialog } from './CombineProductsDialog';
import { SplitOffDialog } from './SplitOffDialog';
import { CreateImportKeyDialog } from './CreateImportKeyDialog';
import { JoinRowChips, toursOf, type JoinRowInfo } from './JoinRowChips';
import { PolicyNotes, PolicySelect } from './PolicyPicker';
import {
  mappingsForOption,
  mintForProduct,
  policyStatus,
  stateColor,
  stateLabel,
} from './mappingPolicy';
import {
  clearAll as clearJoins,
  detach,
  keyGuard,
  launchSummary,
  launchSummaryText,
  membersOf,
  retainSelected,
  roleOf,
  setSplitOff,
  toStartPayload,
  type JoinContext,
  type JoinState,
} from './productJoins';
import { filterProducts, highlightSegments } from './productFilter';
import { useIntegrationFeatures, useIntegrationRow, type SyncScope } from './SyncScopePicker';

/** The cp-import-v2 containers a product can build into, in dependency order.
 *  Only offered when the integration advertises `product_entity_selection`. */
/** Above this many products, an AI-enriched run asks before spending. */
export const AI_CONFIRM_THRESHOLD = 10;

/** Whether starting this run should ask first.
 *
 *  AI enrichment is one metered model call per product, so a large selection is
 *  a real amount of money committed by one click. A deterministic run is free
 *  and never asks, however large.
 */
export function needsAiConfirmation(aiEnabled: boolean, selectedCount: number): boolean {
  return aiEnabled && selectedCount > AI_CONFIRM_THRESHOLD;
}

const ENTITY_KINDS = [
  { id: 'activities', label: 'Activities' },
  { id: 'itineraries', label: 'Itineraries' },
  { id: 'tours', label: 'Tours' },
] as const;

/** Labels whose value is not the same on every product sharing a name.
 *
 *  A restech's catalogue routinely repeats a name across ids (Prio's staging
 *  catalogue has three such pairs), and those rows are identical on most facets
 *  — supplier, languages, admission type. Emphasising only the facets that
 *  actually vary is what turns the column into an answer to "which of these two
 *  is which". Comparison is against the rows currently on screen, which for this
 *  un-paginated list is the whole fetched catalogue.
 */
export function differingFacets(products: ProductListItem[]): Map<string, Set<string>> {
  const byName = new Map<string, ProductListItem[]>();
  for (const p of products) {
    // Unnamed rows are not a name collision — grouping them would claim two
    // unrelated products "differ in X".
    if (!p.name) continue;
    const group = byName.get(p.name);
    if (group) group.push(p);
    else byName.set(p.name, [p]);
  }

  const result = new Map<string, Set<string>>();
  for (const [name, group] of byName) {
    if (group.length < 2) continue;
    const labels = new Set(group.flatMap((p) => (p.facets ?? []).map((f) => f.label)));
    const differing = new Set<string>();
    for (const label of labels) {
      // An absent facet counts as a difference: one row priced, one not.
      const values = new Set(
        group.map((p) => (p.facets ?? []).find((f) => f.label === label)?.value),
      );
      if (values.size > 1) differing.add(label);
    }
    if (differing.size) result.set(name, differing);
  }
  return result;
}

/** Products whose departures can be expanded.
 *
 *  A product with no children is not expandable, and counting it would leave
 *  "expand all" permanently short of the all-expanded state — the toggle would
 *  never flip to "collapse all".
 */
export function expandableProductIds(products: ProductListItem[]): string[] {
  return products.filter((p) => p.id && p.children.length > 0).map((p) => p.id as string);
}

/** This integration's own config dialog, or null when the row is not known.
 *
 *  The path is the integration ROW id — `restech` plus the account slug, resolved
 *  by the backend — not the restech name, because one restech can have several
 *  accounts and only one of them is the misconfigured one. The row is already on
 *  the page for the source_id field.
 *
 *  `/tour-operators/:id/integrations/:rowId` opens the edit dialog on its config
 *  subtab (IntegrationEditDialog treats any segment other than webhooks /
 *  subscriptions as config), which is where credentials and keys are entered.
 */
export function integrationConfigUrl(
  tenantId: number | undefined,
  rowId: string | undefined,
): string | null {
  if (!tenantId || !rowId) return null;
  return `/tour-operators/${tenantId}/integrations/${encodeURIComponent(rowId)}`;
}

/** The same page, offered on a FAILED fetch — null unless the failure really is
 *  a missing-credentials one: a network blip or a vendor 500 must not offer a
 *  config page as though the operator had mis-typed something. */
export function credentialsConfigUrl(
  error: unknown,
  tenantId: number | undefined,
  rowId: string | undefined,
): string | null {
  if (!isMissingCredentials(error)) return null;
  return integrationConfigUrl(tenantId, rowId);
}

/** What a fetch task that did not complete tells the operator, or null when
 *  it did. `configurable` = the failure is fixed on the integration's config
 *  page (no credentials, or an account the integration cannot address), so
 *  the page offers that link; a vendor outage must not. */
export function fetchTaskProblem(
  task: Pick<BackgroundTaskDetail, 'state' | 'error_message' | 'metrics'>,
): { severity: 'error' | 'info'; message: string; configurable: boolean } | null {
  if (task.state === TASK_STATE.CANCELLED) {
    return { severity: 'info', message: 'The product fetch was cancelled.', configurable: false };
  }
  if (task.state !== TASK_STATE.FAILED) return null;
  const outcome = task.metrics?.outcome;
  return {
    severity: 'error',
    message: task.error_message || 'Fetching products failed — see the fetch log.',
    configurable: outcome === 'missing_credentials' || outcome === 'blocked',
  };
}

/** The second half of a blocked listing's guidance: WHERE the named config
 *  keys are edited.
 *
 *  Deliberately not a restatement of the value's shape — `user_key` is a
 *  structured field with its own editor now (the Integrations config dialog
 *  and the Preflight tab open the same one), so what an operator is missing is
 *  the location, not the JSON. The backend's `detail` already said what is
 *  wrong; this says where to go.
 */
export function configKeysSentence(keys: string[] | undefined): string | null {
  if (!keys || keys.length === 0) return null;
  const names = keys.join(', ');
  const verb = keys.length === 1 ? 'is' : 'are';
  return `${names} ${verb} edited on this operator's Integrations tab, where the field opens its own editor rather than a text box.`;
}

/** Whether the fetched listing may be imported, and why not.
 *
 *  The listing payload answers for THIS tenant/account on every read (plan
 *  2026-10-08 P1-A): a gated restech advertises `product_sync` everywhere but
 *  imports only where the tenant opted in. The integration's `supports` only
 *  stands in for a listing from a backend older than the field.
 */
export function importGate(
  listing: Pick<ProductsResponse, 'can_import' | 'import_blocker'> | null,
  integrationFeatures: string[],
): { canImport: boolean; blocker: ImportBlocker | null } {
  if (listing && typeof listing.can_import === 'boolean') {
    return {
      canImport: listing.can_import,
      blocker: listing.can_import ? null : (listing.import_blocker ?? null),
    };
  }
  return { canImport: integrationFeatures.includes('product_sync'), blocker: null };
}

/** The listing-only notice: why nothing can be imported here, and where that is
 *  changed. Keyed off the blocker's `code`; `detail` is the backend's sentence. */
export function ImportBlockerAlert({
  blocker,
  integration,
  configUrl,
}: {
  blocker: ImportBlocker | null;
  integration: string | null;
  configUrl: string | null;
}) {
  if (blocker?.code === 'catalogue_sync_disabled') {
    const keys = blocker.config_keys.length ? blocker.config_keys : ['catalogue_sync'];
    return (
      <Alert
        severity="warning"
        action={
          configUrl ? (
            <Button size="small" color="inherit" component={RouterLink} to={configUrl} target="_blank">
              Configure
            </Button>
          ) : undefined
        }
      >
        <AlertTitle>Catalogue sync is not enabled for this tenant</AlertTitle>
        {blocker.detail} Config key:{' '}
        {keys.map((k, i) => (
          <Fragment key={k}>
            {i > 0 && ', '}
            <code>{k}</code>
          </Fragment>
        ))}{' '}
        on this operator&apos;s {integration ?? 'integration'} config. The products can still be
        mapped to existing tours by hand.
      </Alert>
    );
  }
  return (
    <Alert severity="info">
      This integration supports catalog listing only: its products cannot be imported here, but
      they can be mapped to existing tours.
    </Alert>
  );
}

/** `text` with every occurrence of `query` marked.
 *
 *  React nodes, never dangerouslySetInnerHTML: `react/no-danger` is an error in
 *  this app, and vendor product names are exactly the content you must not
 *  inject as HTML. Same mark styling as the Find & Replace snippets.
 */
function Highlight({ text, query }: { text: string; query: string }) {
  const segments = highlightSegments(text, query);
  if (segments.length === 1) return <>{text}</>;
  return (
    <>
      {segments.map((s, i) =>
        s.match ? (
          <Box
            key={i}
            component="mark"
            sx={{
              bgcolor: 'warning.light',
              color: 'warning.contrastText',
              px: 0.25,
              borderRadius: 0.5,
            }}
          >
            {s.text}
          </Box>
        ) : (
          <Fragment key={i}>{s.text}</Fragment>
        ),
      )}
    </>
  );
}

function ProductFacets({
  facets,
  emphasise,
}: {
  facets?: ProductFacet[];
  emphasise?: Set<string>;
}) {
  if (!facets?.length) return null;
  return (
    <Typography variant="caption" color="text.secondary" component="span">
      {facets.map((f, i) => {
        const strong = emphasise?.has(f.label) ?? false;
        return (
          <Fragment key={f.label}>
            {i > 0 && (
              <Box component="span" sx={{ mx: 0.5 }}>
                ·
              </Box>
            )}
            {/* title, not <Tooltip>: five facets across a few hundred rows is
                more MUI portals than this column is worth. */}
            <Box
              component="span"
              title={f.label}
              sx={{ fontWeight: strong ? 700 : 400, color: strong ? 'text.primary' : undefined }}
            >
              {f.value}
            </Box>
          </Fragment>
        );
      })}
    </Typography>
  );
}

function MappingChips({ mappings }: { mappings: ProductMapping[] | undefined }) {
  if (!mappings?.length) {
    return <Chip size="small" label="unmapped" variant="outlined" color="warning" />;
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {mappings.map((m, i) => (
        <Tooltip
          key={i}
          title={
            <>
              <code>{m.custom_id}</code>
              <br />
              {m.policy.policy
                ? `mapped as: ${m.policy.policy}${m.policy.ambiguous ? ' (or a ticket — the listing carries neither, so this cannot be decided)' : ''}`
                : (m.policy.reason ?? 'unrecognised')}
            </>
          }
        >
          <Chip
            size="small"
            color={m.via === 'custom_id' ? 'primary' : 'default'}
            variant="outlined"
            label={`${m.tour.name ?? `#${m.tour.id}`}${m.via === 'custom_id' ? ' (primary)' : ''}`}
          />
        </Tooltip>
      ))}
    </Stack>
  );
}

/** What the selected policy would do to this product, and whether it is already
 *  mapped some other way. Recomputed on every policy change — the whole point
 *  of the control. */
function PolicyCell({
  policy,
  product,
  mappings,
  joinChips,
}: {
  policy: MappingPolicy | undefined;
  product: ProductListItem;
  mappings: ProductMapping[] | undefined;
  /** How the product takes part in the run's joins (JoinRowChips). */
  joinChips?: ReactNode;
}) {
  const status = policyStatus(policy, product, mappings);
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          variant={status.state === 'mapped' ? 'filled' : 'outlined'}
          color={stateColor(status.state)}
          label={stateLabel(status.state)}
        />
        {status.conflicting && (
          <Tooltip
            title={`Already mapped as ${status.currentPolicies.join(', ')}. Syncing under the selected policy leaves that mapping live unless you skip this product.`}
          >
            <Chip size="small" color="warning" label="other policy" />
          </Tooltip>
        )}
      </Stack>
      {status.minted.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
          {status.minted.length === 1
            ? status.minted[0]?.value
            : `${status.minted.length} ids, e.g. ${status.minted[0]?.value ?? ''}`}
        </Typography>
      )}
      {joinChips}
    </Stack>
  );
}

export interface ProductSyncFormProps {
  /** The tenant / integration / account this form fetches and imports for. */
  scope: SyncScope;
  /** Fixed-tenant hosts (the setup wizard) set this; the form itself never
   *  changes the scope, so today it is documentation for hosts. */
  lockScope?: boolean;
  /** The page injects its SyncScopePicker here so the original single
   *  toolbar row (picker · force checkbox · fetch button) survives extraction. */
  scopeSlot?: ReactNode;
  /** Launch override — the setup wizard routes the launch through its
   *  preflight item (stage + execute) instead of /ops/sync_products/start/. */
  onSubmit?: (input: SyncProductsStartInput) => Promise<{ id: string }>;
  /** Where to go once launched. Default: navigate to the task's page. */
  onLaunched?: (taskId: string) => void;
}

export function ProductSyncForm({ scope, scopeSlot, onSubmit, onLaunched }: ProductSyncFormProps) {
  const { show } = useSnackbar();
  const navigate = useNavigate();
  // Collapsed by default: one Salesforce tour can carry 14 departures, so an
  // expanded catalogue is hundreds of rows an operator has to scroll past to
  // reach the next PRODUCT — which is the thing they actually select.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // The run's mapping policy. Seeded from the tenant's configured default when
  // a listing arrives, then owned by the operator — re-seeding on every fetch
  // would silently undo their choice on a refetch.
  const [policyId, setPolicyId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [force, setForce] = useState(false);
  const [data, setData] = useState<ProductsResponse | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // The run's joins, split-offs and their consents (productJoins). They only
  // ever mention selected products: every selection change goes through
  // `changeSelection`, which drops or cascades what a deselect invalidates.
  const [joinState, setJoinState] = useState<JoinState>(clearJoins);
  // The start's `product_join_invalid` refusal, listed above the launch button
  // with its products flagged, until the joins change or the next launch.
  const [startRefusal, setStartRefusal] = useState<ProductJoinInvalid | null>(null);
  const [combineOpen, setCombineOpen] = useState(false);
  // The product whose split-off is being confirmed, and the tour it leaves.
  const [splitFor, setSplitFor] = useState<{
    product: { id: string; name: string };
    tour: ProductMapping['tour'];
  } | null>(null);
  const [dryRun, setDryRun] = useState(false);
  const [approval, setApproval] = useState(false);
  const [entities, setEntities] = useState<string[]>(ENTITY_KINDS.map((e) => e.id));
  // Parallel (plimport) import is the default; sequential is the escape
  // hatch for isolating a sync that misbehaves under parallelism.
  const [sequential, setSequential] = useState(false);
  const [batchMultiplier, setBatchMultiplier] = useState('1');
  const [fetchPoolSize, setFetchPoolSize] = useState('1');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [delay, setDelay] = useState('0');
  const [drawerFor, setDrawerFor] = useState<string | null>(null);
  const [sourceId, setSourceId] = useState<string | null>(null);
  // True once the operator picks a key themselves. An explicit choice is never
  // overridden -- if they later select kinds it cannot write, the gate explains
  // that rather than silently swapping the key out from under them.
  const [sourcePinned, setSourcePinned] = useState(false);
  const [createKeyOpen, setCreateKeyOpen] = useState(false);
  // Metered. On by default because the enrichment is what makes a
  // FareHarbor catalogue readable -- its day plans, ages and group sizes
  // exist only as prose -- but every run is chargeable, so a large
  // selection asks first.
  const [enrichWithAi, setEnrichWithAi] = useState(true);
  // The run waiting for the AI-cost confirmation: how many products, and how to start it.
  const [confirmAi, setConfirmAi] = useState<{ count: number; go: () => void } | null>(null);
  // Catalogue mode: the vendor listing (hand mappings) is secondary and starts folded.
  const [listingOpen, setListingOpen] = useState(false);
  // The `list_products` task fetching the catalogue, while it runs.
  const [fetchTaskId, setFetchTaskId] = useState<string | null>(null);
  // Why the last fetch produced no listing (failed / cancelled / expired),
  // shown in place of the table until the next fetch.
  const [fetchProblem, setFetchProblem] = useState<{
    severity: 'error' | 'info' | 'warning';
    message: string;
    configurable: boolean;
    taskId: string | null;
  } | null>(null);
  // The task whose details dialog is open in place (never navigates away).
  const [logsTaskId, setLogsTaskId] = useState<string | null>(null);

  // A different tenant/integration/account invalidates the fetched catalogue —
  // the same reset the page's picker onChange performed before extraction.
  // The fetch task keeps running (it warms the cache; coming back joins it),
  // but this page stops watching it.
  const scopeKey = `${scope.tenant?.id ?? ''}|${scope.integration ?? ''}|${scope.account ?? ''}`;
  const scopeKeyRef = useRef(scopeKey);
  scopeKeyRef.current = scopeKey;
  useEffect(() => {
    setData(null);
    setFetchTaskId(null);
    setFetchProblem(null);
  }, [scopeKey]);

  const features = useIntegrationFeatures();
  const integrationFeatures = scope.integration ? (features[scope.integration] ?? []) : [];
  // Import is decided by the listing payload (per tenant/account), not by the
  // integration's `supports` — see importGate.
  const { canImport, blocker: importBlocker } = importGate(data, integrationFeatures);
  // An integration whose restech keeps per-product catalogue rows (it has a settings
  // registry; any other answers 400) gets the catalogue section: rows, settings, review
  // queue, runs. Decided by the backend, never by an integration name.
  const catalogueScope = catalogueScopeOf(scope);
  const registryQuery = useCatalogueRegistry(catalogueScope);
  const catalogueMode = Boolean(catalogueScope && registryQuery.data);
  const invalidateCatalogue = useInvalidateCatalogue(scope.tenant?.id);
  // With the catalogue gate open, the catalogue section is where products are chosen and
  // launched; the vendor listing below is only for looking at hand mappings.
  const listingSecondary = catalogueMode && canImport;
  // Gated on the capability, never on an integration name: the builder has to
  // actually read `product_entities`, and only the backend knows if it does.
  const canPickEntities = integrationFeatures.includes('product_entity_selection');
  // Same rule as entity selection: gate on the capability, never on an
  // integration name. Only an integration with an enrichment pass
  // advertises this.
  const canEnrich = integrationFeatures.includes('product_ai_enrichment');
  const aiOn = canEnrich && enrichWithAi;
  // Combining products into one tour and splitting a product off the tour
  // its key is an additional ID of (plan 2026-10-11 product-sync joins).
  // Capability-gated like the rest; the listing carries the same names.
  const hasFeature = (f: string) =>
    integrationFeatures.includes(f) || (data?.features ?? []).includes(f);
  const canJoin = hasFeature('product_join');
  const canSplitOff = hasFeature('product_split_off');
  // The builder itself resolves a key that is another tour's additional ID to
  // that tour and builds it (Juniper), so no such product is "not built".
  const resolvesAliasedKeys = hasFeature('product_alias_resolution');
  // The listing's own run only: the catalogue section keys tours by each
  // product's saved settings instead.
  const joinsOn = (canJoin || canSplitOff) && !listingSecondary;

  // The source this integration is configured to write under. Seeding the
  // picker with it is the point: the field then states which Import_Source the
  // run will stamp on every <tour>, <activity> and <itinerary>, instead of
  // leaving it to be discovered from the generated XML.
  const row = useIntegrationRow(scope);
  const configuredSourceId = row?.source_id ?? null;
  // The integration's own config dialog — where a blocked listing is unblocked.
  const configUrl = integrationConfigUrl(scope.tenant?.id, row?.id);
  useEffect(() => {
    // A different integration or account means a different configured source
    // and possibly a different set of keys, so any pin from the last one goes.
    setSourcePinned(false);
    setSourceId(configuredSourceId);
  }, [configuredSourceId, scope.integration, scope.account]);

  // The chosen import key's permissions. A key that cannot create tours makes
  // the run import nothing WITHOUT failing -- the importer drops each entity
  // and logs at debug -- so this is checked before the button, not after.
  const { data: importSources } = useImportSources(scope.tenant?.id ?? 0);

  // Picking a key by hand should be for when you want a SPECIFIC one, not a
  // step everyone pays on every run. So the configured key is kept when it can
  // write the selected kinds, and otherwise the first key that can is chosen --
  // re-derived when the kinds change, since that is exactly when the previous
  // choice can stop being valid.
  //
  // When nothing matches, the current value is left alone rather than cleared:
  // the gate below then names what is missing and offers to mint a key, which
  // is more use than an empty field.
  const autoSourceId = useMemo(
    () => autoSelectKey(importSources ?? [], entities, sourceId),
    [importSources, entities, sourceId],
  );

  const effectiveSourceId = sourcePinned ? sourceId : autoSourceId;
  const selectedKey = (importSources ?? []).find((s) => s.code === effectiveSourceId) ?? null;
  const autoChosen = !sourcePinned && effectiveSourceId !== configuredSourceId;
  // Judged only when the list is in hand. Listing keys needs
  // `console_restech_view`, which this route does not require, so an operator
  // without it would otherwise find the button permanently disabled for a run
  // that is perfectly fine. The backend runs the same check and answers 400
  // with the same explanation, so the worst case here is a late refusal rather
  // than a silent no-op -- which is the failure this whole gate exists to stop.
  const keyLacking = selectedKey ? lackingKinds(selectedKey.allow_import, entities) : [];

  /** Report a failure, and when it is "no credentials", offer the page that
   *  fixes it. Opened in a new tab on purpose: the operator has a product
   *  selection and a set of run options here, and navigating away discards both.
   */
  const showError = (e: unknown) => {
    const configUrl = credentialsConfigUrl(e, scope.tenant?.id, row?.id);
    show((e as Error).message, 'error', {
      ...(configUrl
        ? {
            // Longer than the 5s default: the message names the missing keys and
            // the action is only useful while it is still on screen to be clicked.
            autoHideDuration: 20000,
            action: {
              label: 'Configure',
              onClick: () => window.open(configUrl, '_blank', 'noopener,noreferrer'),
            },
          }
        : {}),
    });
  };

  const applyListing = (r: ProductsResponse) => {
    setData(r);
    // A fetch that ran the `list_products` task also refreshed the catalogue rows.
    if (catalogueMode) void invalidateCatalogue();
    setSelected(new Set());
    setJoinState(clearJoins());
    setStartRefusal(null);
    setExpanded(new Set());
    setPolicyId((current) => {
      // Keep an explicit choice across a refetch; otherwise take the tenant's
      // configured default, and fall back to the first creatable policy so
      // the control is never empty when one exists.
      const creatable = r.policies.filter((p) => p.flows.includes('catalogue') && p.mintable);
      if (current && creatable.some((p) => p.id === current)) return current;
      if (r.default_policy && creatable.some((p) => p.id === r.default_policy)) {
        return r.default_policy;
      }
      return creatable[0]?.id ?? null;
    });
  };

  // Every response is checked against the scope it was asked for: the
  // operator may have switched tenant/integration/account while it was in
  // flight, and the old catalogue must not land under the new scope.
  const fetchMut = useMutation({
    mutationFn: (opts: { force: boolean; scopeKey: string }) =>
      syncApi.startProductFetch({
        tour_operator_id: scope.tenant!.id,
        integration: scope.integration!,
        account: scope.account,
        force: opts.force,
      }),
    onMutate: () => setFetchProblem(null),
    onSuccess: (r, vars) => {
      if (vars.scopeKey !== scopeKeyRef.current) return;
      if (r.status === 'ready') {
        applyListing(r.listing);
        return;
      }
      setFetchTaskId(r.task_id);
      if (r.adopted) show('A fetch for this account was already running — following it.', 'info');
    },
    onError: (e, vars) => {
      if (vars.scopeKey === scopeKeyRef.current) showError(e);
    },
  });

  // The listing the task just fetched, read back from the cache.
  const readFetchedMut = useMutation({
    mutationFn: (vars: { taskId: string; scopeKey: string }) =>
      syncApi.getProducts({
        tour_operator_id: scope.tenant!.id,
        integration: scope.integration!,
        account: scope.account,
        task_id: vars.taskId,
      }),
    onSuccess: (r, vars) => {
      if (vars.scopeKey === scopeKeyRef.current) applyListing(r);
    },
    onError: (e, vars) => {
      if (vars.scopeKey !== scopeKeyRef.current) return;
      if (isCatalogueNotLoaded(e)) {
        // Evicted in the seconds since the task finished, or the product
        // template changed mid-fetch (the rows were rendered with the old
        // one and are keyed under it). Either way: fetch again.
        setFetchProblem({
          severity: 'warning',
          message: 'The fetched catalogue was no longer in the cache when the page read it. Fetch it again.',
          configurable: false,
          taskId: vars.taskId,
        });
      } else {
        showError(e);
      }
    },
  });

  const onFetchTerminal = (task: BackgroundTaskDetail) => {
    setFetchTaskId(null);
    if (task.state === TASK_STATE.COMPLETED) {
      readFetchedMut.mutate({ taskId: task.id, scopeKey });
      return;
    }
    const problem = fetchTaskProblem(task);
    if (problem) setFetchProblem({ ...problem, taskId: task.id });
  };

  // "Loading" spans the whole fetch: the request, the background task, and
  // reading the result back.
  const fetching = fetchMut.isPending || fetchTaskId !== null || readFetchedMut.isPending;
  const fetchNow = (forceFetch: boolean) => fetchMut.mutate({ force: forceFetch, scopeKey });

  const startMut = useMutation({
    mutationFn: (productIds: string[]) =>
      (onSubmit ?? opsApi.startSyncProducts)({
        tour_operator_id: scope.tenant!.id,
        integration_name: scope.integration!,
        account: scope.account,
        product_ids: productIds,
        dry_run: dryRun,
        approval_required: approval && !dryRun,
        // Omit when every kind is wanted, so integrations without the feature
        // are never sent a field the backend would reject.
        ...(canPickEntities && entities.length < ENTITY_KINDS.length ? { entities } : {}),
        // The key shown in the picker, not the raw state: when the configured
        // key cannot write the selected kinds the form auto-picks one that
        // can, and `sourceId` still holds the configured one. Sending that
        // would launch on a key the gate had already rejected, and the
        // backend then refuses a key the operator never saw.
        source_id: effectiveSourceId ?? undefined,
        use_plimport: !sequential,
        batch_multiplier: Math.round(Number(batchMultiplier)) || 1,
        fetch_pool_size: Math.round(Number(fetchPoolSize)) || 1,
        // The form the created tours are keyed by. Re-validated server-side
        // against what this integration's builder can actually mint. A
        // catalogue-synced tenant keys tours by each product's settings instead.
        ...(policyId && !listingSecondary ? { mapping_policy: policyId } : {}),
        import_delay: sequential ? Number(delay) || 0 : 0,
        // Omitted unless supported, so an integration without the pass is
        // never sent a field that would silently do nothing.
        ...(canEnrich ? { enrich_with_ai: enrichWithAi } : {}),
        // Always all three, `[]` and null included, whatever the integration
        // supports and in catalogue mode too: the setup wizard deep-merges the
        // staged payload, so an omitted key would keep a previous run's joins,
        // even one staged for another integration or account.
        ...toStartPayload(joinsOn ? joinState : clearJoins()),
      }),
    onMutate: () => setStartRefusal(null),
    onSuccess: (r) => {
      show(
        dryRun
          ? 'Build started — no import.'
          : approval
            ? 'Build started. The task will pause for your approval before importing.'
            : 'Product import started.',
        'success',
      );
      if (onLaunched) onLaunched(r.id);
      else navigate(`/bgtasks/${r.id}`);
    },
    onError: (e) => {
      const refusal = joinsOn ? syncApi.productJoinInvalid(e) : null;
      if (!refusal) {
        showError(e);
        return;
      }
      setStartRefusal(refusal);
      show('The run was refused: see the problems listed above the import button.', 'error');
    },
  });

  /** Start a run over `count` products whose ids `resolveIds` yields — asking first when AI
   *  enrichment would make it expensive. The catalogue resolves its ids only now (every
   *  included product, or every row matching the filters). */
  const launch = (count: number, resolveIds: () => Promise<string[]>) => {
    const go = () => {
      void resolveIds()
        .then((ids) => {
          if (!ids.length) show('Nothing to import: no product resolved.', 'info');
          else startMut.mutate(ids);
        })
        .catch(showError);
    };
    if (needsAiConfirmation(aiOn, count)) setConfirmAi({ count, go });
    else go();
  };

  const filtered = useMemo(() => filterProducts(data?.products ?? [], query), [data, query]);

  // Selection follows what the operator can SEE. Selecting rows hidden behind a
  // filter is the kind of surprise that ends in an unintended import.
  const productIds = useMemo(
    () => filtered.map((f) => f.product.id).filter((x): x is string => Boolean(x)),
    [filtered],
  );

  const pageStart = page * pageSize;
  const paged = useMemo(
    () => filtered.slice(pageStart, pageStart + pageSize),
    [filtered, pageStart, pageSize],
  );

  // A filter that shrinks the list past the current page would otherwise leave
  // the operator on an empty one with no way back except paging.
  useEffect(() => {
    setPage(0);
  }, [query, data]);
  const allSelected = productIds.length > 0 && productIds.every((id) => selected.has(id));
  const selectedPolicy: MappingPolicy | undefined = data?.policies.find((p) => p.id === policyId);
  const [applyOpen, setApplyOpen] = useState(false);

  // Computed over the WHOLE catalogue, not the page or the filtered set: whether
  // two products share a name is a fact about the catalogue, and a row should not
  // lose its emphasis just because its twin fell off the current page.
  const emphasised = useMemo(() => differingFacets(data?.products ?? []), [data]);

  /** Every selection change: the joins follow it (a deselected member leaves
   *  its group, a deselected primary's members are built on their own). */
  const changeSelection = (next: Set<string>) => {
    setSelected(next);
    changeJoins(retainSelected(joinState, next));
  };
  const changeJoins = (next: JoinState) => {
    if (next === joinState) return;
    setJoinState(next);
    setStartRefusal(null);
  };

  const toggleAll = () => {
    changeSelection(allSelected ? new Set() : new Set(productIds));
  };

  // Only products that actually have departures — an "expand all" that counts
  // childless rows would never reach the all-expanded state.
  const expandableIds = useMemo(() => expandableProductIds(paged.map((f) => f.product)), [paged]);
  const allExpanded = expandableIds.length > 0 && expandableIds.every((id) => expanded.has(id));
  const toggleAllExpanded = () => {
    setExpanded(allExpanded ? new Set() : new Set(expandableIds));
  };
  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    changeSelection(next);
  };

  // What the join rules read: the whole listing, the run's policy and selection.
  const joinCtx: JoinContext | null = data
    ? {
        products: data.products,
        mappings: data.mappings,
        policy: selectedPolicy,
        selected,
        resolvesAliasedKeys,
        canSplitOff,
      }
    : null;
  const nameById = useMemo(
    () => new Map((data?.products ?? []).flatMap((p) => (p.id ? [[p.id, p.name || p.id]] : []))),
    [data],
  );
  const nameOf = (id: string) => nameById.get(id) ?? id;
  const refusalsFor = (id: string) =>
    (startRefusal?.problems ?? []).filter((p) => p.product_id === id);

  /** The chips of one listing row, or undefined when it takes no part. */
  const joinRowInfo = (p: ProductListItem): JoinRowInfo | null => {
    if (!p.id || !joinCtx || !canImport || listingSecondary) return null;
    const role = roleOf(joinState, p.id);
    const primaryId = role === 'member' ? joinState.joins.get(p.id) : undefined;
    return {
      role,
      members:
        role === 'primary' ? membersOf(joinState, p.id).map((id) => ({ id, name: nameOf(id) })) : [],
      primary: primaryId ? { id: primaryId, name: nameOf(primaryId) } : null,
      guard: keyGuard(joinState, p, joinCtx),
      problems: refusalsFor(p.id),
    };
  };
  /** The tour `p` can be split off (D2): its one key is an additional ID of
   *  exactly one other tour, and the re-sync guard leaves it out. Null when
   *  the integration cannot split off, or when the product is a member (it is
   *  not built) or is built anyway. */
  const splitOffTour = (p: ProductListItem, info: JoinRowInfo): ProductMapping['tour'] | null => {
    if (!canSplitOff || info.role === 'member' || info.guard.verdict !== 'left_out') return null;
    if (!selectedPolicy?.mintable || mintForProduct(selectedPolicy, p).length !== 1) return null;
    const tours = toursOf(info.guard.elsewhere);
    return tours.length === 1 ? (tours[0] ?? null) : null;
  };
  const summaryLine =
    joinCtx && !listingSecondary ? launchSummaryText(launchSummary(joinState, joinCtx)) : null;

  // "Combine into one tour…" works on the selected products that take no part
  // in a group yet, in listing order.
  const combinable = useMemo(
    () =>
      (data?.products ?? []).flatMap((p) =>
        p.id && selected.has(p.id) && roleOf(joinState, p.id) === null ? [p.id] : [],
      ),
    [data, selected, joinState],
  );
  const combineBlocker = !selectedPolicy
    ? 'Pick the mapping policy the tour is keyed by first.'
    : combinable.length < 2
      ? 'Select at least two products that are not combined yet.'
      : null;
  const combineControl = canJoin ? (
    <Tooltip title={combineBlocker ?? ''}>
      <span>
        <Button
          size="small"
          variant="outlined"
          disabled={combineBlocker !== null}
          onClick={() => setCombineOpen(true)}
        >
          Combine into one tour…
        </Button>
      </span>
    </Tooltip>
  ) : null;

  /** The run options and the launch button, for whatever the host is importing: the ticked
   *  listing rows, or the catalogue section's target (ticked, matching or included rows). */
  const renderLaunchPanel = (t: {
    count: number;
    noun: string;
    onLaunch: () => void;
    leading?: ReactNode;
    /** Above the run options: what the run will do with joined products. */
    summary?: ReactNode;
  }) => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {t.summary}
        {canPickEntities && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Build:
            </Typography>
            {ENTITY_KINDS.map((kind) => (
              <FormControlLabel
                key={kind.id}
                control={
                  <Checkbox
                    size="small"
                    checked={entities.includes(kind.id)}
                    onChange={(e) =>
                      setEntities((prev) =>
                        e.target.checked
                          ? ENTITY_KINDS.filter(
                              (k) => k.id === kind.id || prev.includes(k.id),
                            ).map((k) => k.id)
                          : prev.filter((id) => id !== kind.id),
                      )
                    }
                  />
                }
                label={kind.label}
              />
            ))}
            {entities.includes('tours') && !entities.includes('activities') && (
              <Typography variant="caption" color="warning.main">
                Tours reference activities by id — those must already be imported.
              </Typography>
            )}
          </Box>
        )}
        {scope.tenant && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Import source:
            </Typography>
            <Box sx={{ width: 340 }}>
              <ImportSourceWidget
                toId={scope.tenant.id}
                value={effectiveSourceId}
                onChange={(code) => {
                  setSourceId(code);
                  setSourcePinned(true);
                }}
                requiredEntities={entities}
              />
              {selectedKey && (
                <Box sx={{ mt: 0.75 }}>
                  <ImportKeyFlags flags={selectedKey.allow_import} required={entities} />
                </Box>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.2 }}>
              {effectiveSourceId == null
                ? 'Required — every tour, activity and itinerary is stamped with it.'
                : sourcePinned
                  ? effectiveSourceId === configuredSourceId
                    ? "The integration's configured source."
                    : 'Overrides the configured source for this run only.'
                  : autoChosen
                    ? 'Chosen automatically — the first key that can write these kinds. Pick another to override.'
                    : "The integration's configured source."}
            </Typography>
          </Box>
        )}

        {scope.tenant && keyLacking.length > 0 && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => setCreateKeyOpen(true)}>
                Create a key
              </Button>
            }
          >
            <AlertTitle>This import key cannot write {keyLacking.join(', ')}</AlertTitle>A
            missing permission does not fail the import — the entity is dropped and logged at
            debug — so the run would report success and import nothing. Pick a key with those
            permissions, or create one granting exactly what this run needs.
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {t.leading}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
              />
            }
            label="Dry run (build XML artifacts, skip import)"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={approval && !dryRun}
                disabled={dryRun}
                onChange={(e) => setApproval(e.target.checked)}
              />
            }
            label="Review before import (approval step)"
          />
          {canEnrich && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={enrichWithAi}
                  onChange={(e) => setEnrichWithAi(e.target.checked)}
                />
              }
              label={
                <Tooltip title="Uses AI to recover the day plan, ticket types, duration, group size and ages that this vendor only states as prose. Costs money per product; the running total appears in the task details.">
                  <span>AI enrichment</span>
                </Tooltip>
              }
            />
          )}
          <Tooltip title="Parallel import (default) merges every selected product into one import run and fans it out over the import workers. Sequential runs one product at a time — useful to isolate a sync that misbehaves under parallelism.">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={sequential}
                  disabled={dryRun}
                  onChange={(e) => setSequential(e.target.checked)}
                />
              }
              label="Sequential import"
            />
          </Tooltip>
          <TextField
            size="small"
            label="Delay between imports (s)"
            type="number"
            value={delay}
            onChange={(e) => setDelay(e.target.value)}
            // Meaningless between the entity batches of one merged
            // parallel run — only the sequential loop sleeps.
            disabled={dryRun || !sequential}
            inputProps={{ min: 0, max: 300, step: 0.5 }}
            sx={{ width: 210 }}
          />
          <Button
            variant="contained"
            color={dryRun ? 'secondary' : 'primary'}
            disabled={
              t.count === 0 ||
              entities.length === 0 ||
              // Without a source the builder raises after the task is
              // already queued; refusing here costs a failed run.
              effectiveSourceId == null ||
              // A key short of a permission is worse than no key: the run
              // succeeds and imports nothing.
              keyLacking.length > 0 ||
              startMut.isPending
            }
            onClick={t.onLaunch}
          >
            {`${dryRun ? 'Build' : 'Import'} ${t.count} ${t.noun}`}
          </Button>
        </Box>
        <Box>
          <Button
            size="small"
            color="inherit"
            startIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setAdvancedOpen((v) => !v)}
            sx={{ color: 'text.secondary' }}
          >
            Advanced
          </Button>
          <Collapse in={advancedOpen}>
            <Box
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', pt: 1 }}
            >
              <TextField
                size="small"
                label="Batch multiplier"
                type="number"
                value={batchMultiplier}
                onChange={(e) => setBatchMultiplier(e.target.value)}
                disabled={dryRun || sequential}
                inputProps={{ min: 1, max: 10, step: 1 }}
                helperText="Parallel mode: batches processed concurrently (1–10)."
                sx={{ width: 230 }}
              />
              <TextField
                size="small"
                label="Fetch pool size"
                type="number"
                value={fetchPoolSize}
                onChange={(e) => setFetchPoolSize(e.target.value)}
                inputProps={{ min: 1, max: 8, step: 1 }}
                helperText="Concurrent vendor fetches while building (1–8) — mind the vendor's rate limits."
                sx={{ width: 300 }}
              />
            </Box>
          </Collapse>
        </Box>
      </Box>
  );

  const scopeReady = Boolean(scope.tenant && scope.integration);

  // Mapping is separate from importing: a ventrata or bokun tenant can map its whole
  // catalogue here without any product sync existing for it, so the control is shown in
  // both the import and the listing-only branch.
  const mapSelectedControl = (
    <>
      <Typography variant="body2">{selected.size} selected</Typography>
      <Button
        size="small"
        variant="outlined"
        disabled={selected.size === 0 || !selectedPolicy}
        onClick={() => setApplyOpen(true)}
      >
        Map selected as {selectedPolicy?.label ?? '…'}
      </Button>
    </>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center' }}>
        {scopeSlot}
        <FormControlLabel
          control={
            <Checkbox size="small" checked={force} onChange={(e) => setForce(e.target.checked)} />
          }
          label="Force fetch (skip 1h cache)"
        />
        <Button
          variant="contained"
          disabled={!scopeReady || fetching}
          onClick={() => fetchNow(force)}
          startIcon={fetching ? <CircularProgress size={16} /> : undefined}
        >
          {fetchTaskId ? 'Fetching…' : 'Fetch products'}
        </Button>
      </Box>

      {fetchTaskId && (
        <Box sx={{ mb: 2 }}>
          <TaskProgressCard
            key={fetchTaskId}
            taskId={fetchTaskId}
            onTerminal={onFetchTerminal}
            onOpen={() => setLogsTaskId(fetchTaskId)}
          />
        </Box>
      )}

      {fetchProblem && !fetchTaskId && (
        <Alert
          severity={fetchProblem.severity}
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              {fetchProblem.configurable && configUrl && (
                <Button size="small" color="inherit" component={RouterLink} to={configUrl} target="_blank">
                  Configure
                </Button>
              )}
              {fetchProblem.taskId && (
                <Button size="small" color="inherit" onClick={() => setLogsTaskId(fetchProblem.taskId)}>
                  View log
                </Button>
              )}
            </Stack>
          }
        >
          {fetchProblem.message}
        </Alert>
      )}

      {catalogueMode && catalogueScope && registryQuery.data && (
        <CatalogueSection
          scope={catalogueScope}
          registry={registryQuery.data}
          configUrl={configUrl}
          refreshing={fetching}
          onRefresh={() => fetchNow(true)}
          renderLaunch={(t: LaunchTarget) =>
            renderLaunchPanel({
              count: t.count,
              noun: t.noun,
              onLaunch: () => launch(t.count, t.resolveIds),
            })
          }
        />
      )}

      {data && listingSecondary && (
        <Button
          size="small"
          color="inherit"
          startIcon={listingOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setListingOpen((v) => !v)}
          sx={{ color: 'text.secondary', mb: 1 }}
        >
          {listingOpen ? 'Hide' : 'Show'} the vendor listing and hand mappings
        </Button>
      )}

      <Collapse in={!listingSecondary || listingOpen}>
        {data && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {data.count} products from {data.integration}
                {data.account ? `/${data.account}` : ''} · {data.from_cache ? 'cached' : 'fresh'} at{' '}
                {new Date(data.fetched_ts).toLocaleString()}
              </Typography>
              {data.fetch_task_id && (
                <Button size="small" onClick={() => setLogsTaskId(data.fetch_task_id)}>
                  Fetch log
                </Button>
              )}
              <Button
                size="small"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={() => fetchNow(true)}
                disabled={fetching}
              >
                Refetch
              </Button>
              {!canImport && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={
                    importBlocker?.code === 'catalogue_sync_disabled'
                      ? 'listing only — catalogue sync is not enabled for this tenant'
                      : 'listing only — this integration cannot import products'
                  }
                />
              )}
            </Box>

            {/* A listing the integration knows is empty by construction.
                Everything below — filters, policy, table, launch — would be
                furniture around nothing, so the guidance takes their place:
                zero rows and zero explanation is the state this field exists
                to end. */}
            {data.reason && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  configUrl ? (
                    <Button
                      size="small"
                      color="inherit"
                      component={RouterLink}
                      to={configUrl}
                      target="_blank"
                    >
                      Configure
                    </Button>
                  ) : undefined
                }
              >
                <AlertTitle>No catalogue to fetch</AlertTitle>
                {data.reason.detail}
                {configKeysSentence(data.reason.config_keys) && (
                  <> {configKeysSentence(data.reason.config_keys)}</>
                )}
              </Alert>
            )}
          </>
        )}

        {data && !data.reason && (
          <>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 1 }}
            >
              <TextField
                size="small"
                placeholder="Filter by id, name, departure or detail…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ width: 380 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <PolicySelect policies={data.policies} value={policyId} onChange={setPolicyId} />
              {query.trim() && (
                <Typography variant="body2" color="text.secondary">
                  {filtered.length} of {data.products.length} products
                </Typography>
              )}
            </Stack>

            <PolicyNotes
              policies={data.policies}
              value={policyId}
              configuredDefault={data.default_policy}
              productTemplate={data.product_template}
              matchingTemplates={data.matching_templates}
              pairingError={data.template_pairing_error}
            />

            {data.truncated && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Listing stopped at {data.limit.toLocaleString()} products</AlertTitle>
                The catalogue is larger than the fetch ceiling. Products past it are missing here and
                cannot be selected, and their existing mappings show up as unexplained below — they
                are not delisted.
              </Alert>
            )}

            {data.unexplained_mappings.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {data.unexplained_mappings.length} mapping
                {data.unexplained_mappings.length === 1 ? '' : 's'} on this tenant match no product in
                this listing — a delisted product, or an ID written by hand. They are left alone; the
                external-ID grid is where to review them.
              </Alert>
            )}

            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={selected.size > 0 && !allSelected}
                        onChange={toggleAll}
                      />
                    </TableCell>
                    <TableCell padding="checkbox">
                      {expandableIds.length > 0 && (
                        <Tooltip title={allExpanded ? 'Collapse all' : 'Expand all departures'}>
                          <IconButton size="small" onClick={toggleAllExpanded}>
                            {allExpanded ? (
                              <UnfoldLessIcon fontSize="small" />
                            ) : (
                              <UnfoldMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell width={220}>External ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Mapped tours</TableCell>
                    <TableCell width={190}>Under this policy</TableCell>
                    <TableCell width={110} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map(({ product: p, children, matchedViaChild }, idx) => (
                    <Fragment key={p.id ?? `row-${idx}`}>
                      <TableRow hover>
                        <TableCell padding="checkbox">
                          {p.id && (
                            <Checkbox
                              size="small"
                              checked={selected.has(p.id)}
                              onChange={() => toggleOne(p.id!)}
                            />
                          )}
                        </TableCell>
                        <TableCell padding="checkbox">
                          {p.id && children.length > 0 && (
                            <IconButton
                              size="small"
                              onClick={() => toggleExpanded(p.id!)}
                              aria-label={
                                expanded.has(p.id)
                                  ? `Collapse ${p.name}`
                                  : `Expand ${p.children.length} departures of ${p.name}`
                              }
                            >
                              {expanded.has(p.id) ? (
                                <ExpandLessIcon fontSize="small" />
                              ) : (
                                <ExpandMoreIcon fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>
                          <code>{p.id ? <Highlight text={p.id} query={query} /> : '—'}</code>
                        </TableCell>
                        <TableCell>
                          <Highlight text={p.name} query={query} />
                        </TableCell>
                        <TableCell>
                          <ProductFacets facets={p.facets} emphasise={emphasised.get(p.name)} />
                        </TableCell>
                        <TableCell>
                          <MappingChips mappings={p.id ? data.mappings[p.id] : undefined} />
                        </TableCell>
                        <TableCell>
                          <PolicyCell
                            policy={selectedPolicy}
                            product={p}
                            mappings={p.id ? data.mappings[p.id] : undefined}
                            joinChips={(() => {
                              const info = joinRowInfo(p);
                              if (!info) return undefined;
                              const tour = splitOffTour(p, info);
                              return (
                                <JoinRowChips
                                  info={info}
                                  onDetach={() => changeJoins(detach(joinState, p.id!))}
                                  onSplitOff={
                                    tour
                                      ? () =>
                                          setSplitFor({
                                            product: { id: p.id!, name: p.name || p.id! },
                                            tour,
                                          })
                                      : undefined
                                  }
                                  onUndoSplitOff={() =>
                                    changeJoins(setSplitOff(joinState, p.id!, null))
                                  }
                                />
                              );
                            })()}
                          />
                        </TableCell>
                        <TableCell>
                          {p.id && (
                            <Button size="small" onClick={() => setDrawerFor(p.id)}>
                              Mappings
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      {(matchedViaChild || (p.id ? expanded.has(p.id) : true)) &&
                        children.map((child) => (
                          <TableRow key={`${p.id ?? idx}-${child.id}`}>
                            <TableCell />
                            <TableCell />
                            <TableCell sx={{ pl: 4 }}>
                              <code>
                                <Highlight text={child.id} query={query} />
                              </code>
                              {child.kind && (
                                <Chip
                                  size="small"
                                  label={child.kind}
                                  sx={{ ml: 1 }}
                                  variant="outlined"
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              <Highlight text={child.name} query={query} />
                            </TableCell>
                            <TableCell>
                              {/* No emphasis: differingFacets compares PRODUCTS sharing
                                a name. A departure's facets are already only what
                                differs from its siblings, so there is nothing to
                                single out. Most restechs' children carry none. */}
                              <ProductFacets facets={child.facets} />
                            </TableCell>
                            <TableCell>
                              {/* Mappings are keyed by the CATALOGUE PRODUCT, so an
                                option finds its own through the classification
                                rather than by looking itself up — an option id is
                                not a key in that map. */}
                              <MappingChips
                                mappings={mappingsForOption(
                                  p.id ? data.mappings[p.id] : undefined,
                                  child.id,
                                )}
                              />
                            </TableCell>
                            <TableCell />
                            <TableCell>
                              <Button size="small" onClick={() => setDrawerFor(child.id)}>
                                Mappings
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {filtered.length === 0 && query.trim() && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No product, departure or detail contains “{query.trim()}”.
              </Alert>
            )}

            {filtered.length > pageSize && (
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(_, next) => setPage(next)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={[25, 50, 100, 250]}
                // Products, not table rows: a page of 25 renders far more than 25
                // rows once departures are expanded, and counting those would make
                // the number meaningless.
                labelRowsPerPage="Products per page"
                sx={{ mb: 2 }}
              />
            )}

            {canImport ? (
              listingSecondary ? (
                <Typography variant="body2" color="text.secondary">
                  Products are chosen and imported in the catalogue above; this listing shows their hand
                  mappings.
                </Typography>
              ) : (
                renderLaunchPanel({
                  count: selected.size,
                  noun: 'products',
                  onLaunch: () => launch(selected.size, async () => Array.from(selected)),
                  leading: (
                    <>
                      {mapSelectedControl}
                      {combineControl}
                    </>
                  ),
                  summary: (
                    <>
                      {startRefusal && (
                        <Alert severity="error" onClose={() => setStartRefusal(null)}>
                          <AlertTitle>
                            {startRefusal.problems.length > 1
                              ? `The run was refused: ${startRefusal.problems.length} problems with the combined products`
                              : 'The run was refused'}
                          </AlertTitle>
                          {startRefusal.problems.length > 0 ? (
                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                              {startRefusal.problems.map((p, i) => (
                                <li key={i}>
                                  {p.product_id && (
                                    <>
                                      <strong>{nameOf(p.product_id)}</strong> (
                                      <code>{p.product_id}</code>):{' '}
                                    </>
                                  )}
                                  {p.detail}
                                </li>
                              ))}
                            </Box>
                          ) : (
                            startRefusal.detail
                          )}
                        </Alert>
                      )}
                      {summaryLine && (
                        <Typography variant="body2" color="text.secondary">
                          {summaryLine}
                        </Typography>
                      )}
                    </>
                  ),
                })
              )
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* The catalogue section above already states its gate. */}
                {!catalogueMode && (
                  <ImportBlockerAlert
                    blocker={importBlocker}
                    integration={scope.integration}
                    configUrl={configUrl}
                  />
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                  {mapSelectedControl}
                </Box>
              </Box>
            )}
          </>
        )}
      </Collapse>

      <ConfirmDialog
        open={confirmAi !== null}
        title={`Run AI enrichment on ${confirmAi?.count ?? 0} products?`}
        message={
          `AI enrichment makes one metered model call per product, so this run will ` +
          `make about ${confirmAi?.count ?? 0}. The running cost appears in the task details ` +
          `while it works. Turn the checkbox off to build these ${confirmAi?.count ?? 0} ` +
          `products deterministically instead.`
        }
        confirmLabel="Run with AI"
        onConfirm={() => {
          const pending = confirmAi;
          setConfirmAi(null);
          pending?.go();
        }}
        onCancel={() => setConfirmAi(null)}
      />

      {scope.tenant && (
        <CreateImportKeyDialog
          open={createKeyOpen}
          onClose={() => setCreateKeyOpen(false)}
          onCreated={(src) => {
            setSourceId(src.code);
            setSourcePinned(true);
          }}
          tourOperatorId={scope.tenant.id}
          tenantName={scope.tenant.name}
          entities={entities}
        />
      )}

      {data && scope.tenant && (
        <ApplyMappingsDialog
          open={applyOpen}
          onClose={() => setApplyOpen(false)}
          onApplied={() => fetchNow(false)}
          tourOperatorId={scope.tenant.id}
          integration={data.integration}
          account={data.account}
          policy={selectedPolicy}
          products={data.products.filter((p) => p.id && selected.has(p.id))}
          mappings={data.mappings}
          tourFor={(productId) => {
            // The tour a product is already pointed at — whichever mapping
            // named it. A product with none cannot be mapped, and the dialog
            // says so rather than inventing a target.
            const existing = data.mappings[productId];
            return existing?.[0]?.tour.id;
          }}
        />
      )}

      {combineOpen && data && joinCtx && scope.tenant && (
        <CombineProductsDialog
          onClose={() => setCombineOpen(false)}
          onConfirm={(next) => {
            changeJoins(next);
            setCombineOpen(false);
          }}
          scope={{
            tour_operator_id: scope.tenant.id,
            integration: data.integration,
            account: data.account,
          }}
          state={joinState}
          ctx={joinCtx}
          productIds={combinable}
          canSplitOff={canSplitOff}
        />
      )}

      {splitFor && data && scope.tenant && (
        <SplitOffDialog
          onClose={() => setSplitFor(null)}
          onConfirm={() => {
            // Splitting off builds the product, so it joins the selection.
            const next = new Set(selected).add(splitFor.product.id);
            setSelected(next);
            changeJoins(
              setSplitOff(retainSelected(joinState, next), splitFor.product.id, splitFor.tour.id),
            );
            setSplitFor(null);
          }}
          scope={{
            tour_operator_id: scope.tenant.id,
            integration: data.integration,
            account: data.account,
          }}
          policy={selectedPolicy?.id ?? null}
          product={splitFor.product}
          fromTour={splitFor.tour}
          productIds={Array.from(selected)}
        />
      )}

      {logsTaskId && (
        <TaskDetailsDialog taskId={logsTaskId} initialTab="logs" onClose={() => setLogsTaskId(null)} />
      )}

      {drawerFor && scope.tenant && scope.integration && data && (
        <MappingDrawer
          open
          onClose={() => setDrawerFor(null)}
          tourOperatorId={scope.tenant.id}
          integration={scope.integration}
          account={scope.account}
          extValue={drawerFor}
          mappings={data.mappings[drawerFor] ?? []}
          onChanged={() => fetchNow(false)}
        />
      )}
    </Box>
  );
}
